import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAuditLogs,
  fetchAuditLogStats,
  setFilters,
  clearFilters,
  clearError,
  selectAuditLogs,
  selectAuditLogStats,
  selectAuditLogLoading,
  selectAuditLogError,
  selectAuditLogPagination,
  selectAuditLogFilters,
} from "../../../store/slices/auditLogSlice";
import AuditLogAdvancedFilters from "./AuditLogAdvancedFilters.jsx";
import AuditLogChangesTable from "./AuditLogChangesTable.jsx";
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Search,
  FilterList,
  Refresh,
  ExpandMore,
  ExpandLess,
  Visibility,
  HistoryToggleOff,
  Person,
  Clear,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";

const AuditLogDashboard = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const logs = useSelector(selectAuditLogs);
  const stats = useSelector(selectAuditLogStats);
  const loading = useSelector(selectAuditLogLoading);
  const error = useSelector(selectAuditLogError);
  const pagination = useSelector(selectAuditLogPagination);
  const filters = useSelector(selectAuditLogFilters);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    isMobile ? 5 : isTablet ? 10 : 20,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRows, setExpandedRows] = useState({});
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [viewMode, setViewMode] = useState(isMobile ? "cards" : "table");
  const [showFilters, setShowFilters] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadLogs();
    if (!stats) {
      dispatch(fetchAuditLogStats());
    }
  }, [page, rowsPerPage, filters]);

  const loadLogs = () => {
    dispatch(
      fetchAuditLogs({
        page,
        size: rowsPerPage,
        sortBy: "performedAt",
        sortDirection: "desc",
        filters: prepareFilters(),
      }),
    );
  };

  const prepareFilters = () => {
    const prepared = { ...filters };
    
    // Преобразуване на датите в ISO формат
    if (prepared.startDate) {
      const date = new Date(prepared.startDate);
      date.setHours(0, 0, 0, 0);
      prepared.startDate = date.toISOString();
    }
    
    if (prepared.endDate) {
      const date = new Date(prepared.endDate);
      date.setHours(23, 59, 59, 999);
      prepared.endDate = date.toISOString();
    }
    
    // Конвертиране на chitalishteId в число, ако има стойност
    if (prepared.chitalishteId) {
      prepared.chitalishteId = parseInt(prepared.chitalishteId, 10);
    }
    
    // Премахване на празни стойности
    Object.keys(prepared).forEach(key => {
      if (prepared[key] === "" || prepared[key] === null || prepared[key] === undefined) {
        delete prepared[key];
      }
    });
    
    return prepared;
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      setPage(0);
      await loadLogs();
      await dispatch(fetchAuditLogStats());
    } finally {
      setRefreshing(false);
    }
  };

  const handleSearch = () => {
    if (searchTerm.trim()) {
      dispatch(setFilters({ performedBy: searchTerm }));
    } else {
      dispatch(setFilters({ performedBy: "" }));
    }
    setPage(0);
  };

  const handleClearAllFilters = () => {
    dispatch(clearFilters());
    setSearchTerm("");
    setPage(0);
  };

  const handleFilterChange = (key, value) => {
    dispatch(setFilters({ [key]: value }));
    setPage(0);
  };

  const handleAdvancedFilters = (advancedFilters) => {
    dispatch(setFilters(advancedFilters));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleExpandRow = (logId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [logId]: !prev[logId],
    }));
  };

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
  };

  const getActionColor = (action) => {
    switch (action) {
      case "CREATE":
        return "success";
      case "UPDATE":
        return "warning";
      case "DELETE":
        return "error";
      case "EXPORT":
        return "info";
      default:
        return "default";
    }
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return "";
    try {
      const date = new Date(dateTime);
      return date.toLocaleString("bg-BG", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch (e) {
      console.error("Error formatting date:", e);
      return dateTime;
    }
  };

  const prepareDetailedChanges = (log) => {
    // Priority 1: Use oldValues and newValues if available
    if (log.oldValues && log.newValues && log.changedFields) {
      try {
        const oldVals = JSON.parse(log.oldValues);
        const newVals = JSON.parse(log.newValues);
        // changedFields could be a comma-separated string
        const fields = typeof log.changedFields === 'string'
          ? log.changedFields.split(',').map(f => f.trim())
          : Object.keys(log.changedFields);

        const changes = {};
        for (const field of fields) {
          changes[field] = {
            oldValue: oldVals[field],
            newValue: newVals[field],
          };
        }
        if (Object.keys(changes).length > 0) return changes;
      } catch (e) {
        console.error("Error processing old/new values:", e);
        // Fallback to other methods
      }
    }

    // Priority 2: Try to parse changedFields as a complete JSON object
    if (typeof log.changedFields === 'string') {
      try {
        const parsed = JSON.parse(log.changedFields);
        if (typeof parsed === 'object' && parsed !== null && Object.keys(parsed).length > 0) {
          return parsed;
        }
      } catch (e) {
        // It's not a JSON object, treat as plain string in fallback
      }
    }
    
    // Fallback: Return changedFields as a plain string if it's a non-empty string
    if (typeof log.changedFields === 'string' && log.changedFields.trim() !== '') {
      const fields = log.changedFields.split(',').map(f => f.trim());
      const filteredFields = fields.filter(f => f !== 'lastUpdatedAt');
      
      if (filteredFields.length > 0) {
        return filteredFields.join(', ');
      }
      // If only lastUpdatedAt was in the string, we want to show nothing.
      return null;
    }

    return null;
}

  const getEntityTypeLabel = (entityType) => {
    const labels = {
      "CHITALISHTE": "Читалище",
      "INFORMATION_CARD": "Информационна карта",
      "USER": "Потребител",
      "FEEDBACK": "Обратна връзка",
    };
    return labels[entityType] || entityType;
  };

  const renderChangedFields = (log) => {
    const changes = prepareDetailedChanges(log);

    if (!changes) {
      return <Typography variant="body2" color="text.secondary" sx={{mt: 2}}>Няма налична информация за промените по полетата.</Typography>;
    }

    if (typeof changes === 'object') {
      return <AuditLogChangesTable changedFields={changes} />;
    }
    
    // It's a string
    return (
        <Box sx={{ mt: 1, p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
            Променени полета:
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: 'pre-wrap' }}>
            {changes}
            </Typography>
        </Box>
    );
  };

  const renderTableRow = (log) => (
    <React.Fragment key={log.id}>
      <TableRow hover>
        <TableCell>
          <IconButton 
            size="small" 
            onClick={() => handleExpandRow(log.id)}
            aria-label={expandedRows[log.id] ? "Скрий детайли" : "Покажи детайли"}
          >
            {expandedRows[log.id] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Chip
            label={log.action}
            color={getActionColor(log.action)}
            size="small"
          />
        </TableCell>
        <TableCell>{getEntityTypeLabel(log.entityType)}</TableCell>
        <TableCell>
          <Typography variant="body2">
            {log.chitalishteName || "—"}
          </Typography>
          {log.chitalishteRegistrationNumber && (
            <Typography variant="caption" color="text.secondary">
              Рег. №: {log.chitalishteRegistrationNumber}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Chip
            icon={<Person sx={{ fontSize: 14 }} />}
            label={log.performedBy}
            size="small"
            variant="outlined"
          />
        </TableCell>
        <TableCell>{formatDateTime(log.performedAt)}</TableCell>
        <TableCell>
          <Tooltip title="Виж детайли">
            <IconButton size="small" onClick={() => handleViewDetails(log)}>
              <Visibility />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      {expandedRows[log.id] && (
        <TableRow>
          <TableCell colSpan={7}>
            <Box sx={{ p: 2, bgcolor: "grey.50" }}>
              {/* <Typography variant="body2" gutterBottom>
                <strong>Описание:</strong> {log.description || "Няма описание"}
              </Typography> */}
              {log.entityId && (
                <Typography variant="body2" gutterBottom>
                  <strong>ID на обект:</strong> {log.entityId}
                </Typography>
              )}
              {renderChangedFields(log)}
              {log.ipAddress && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>IP адрес:</strong> {log.ipAddress}
                </Typography>
              )}
              {log.userAgent && (
                <Typography variant="body2">
                  <strong>Браузър:</strong> {log.userAgent}
                </Typography>
              )}
            </Box>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );

  const activeFiltersCount = Object.values(filters).filter(
    (value) => value && value.toString().trim() !== ""
  ).length;

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Системни логове
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Проследяване на действия в системата
            </Typography>
          </Box>
          <Button
            startIcon={<Refresh />}
            onClick={handleRefresh}
            variant="contained"
            disabled={refreshing || loading}
          >
            {refreshing ? "Опресняване..." : "Опресни"}
          </Button>
        </Stack>

        {/* Advanced Filters Toggle */}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<FilterList />}
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          sx={{ mb: 2 }}
        >
          {showAdvancedFilters ? "Скрий разширени филтри" : "Покажи разширени филтри"}
        </Button>

        {showAdvancedFilters && (
          <AuditLogAdvancedFilters onApplyFilters={handleAdvancedFilters} />
        )}

        {/* Simple Search and Filters */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Търсене по извършител..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <IconButton 
                    size="small" 
                    onClick={() => setSearchTerm("")}
                    disabled={loading}
                  >
                    <Clear />
                  </IconButton>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              startIcon={<Search />}
            >
              Търсене
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              onClick={handleClearAllFilters}
              disabled={activeFiltersCount === 0 || loading}
              startIcon={<Clear />}
            >
              Изчисти филтри ({activeFiltersCount})
            </Button>
          </Grid>
        </Grid>

        {/* Active Filters Display */}
        {activeFiltersCount > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              Активни филтри:
            </Typography>
            {Object.entries(filters)
              .filter(([_, value]) => value && value.toString().trim() !== "")
              .map(([key, value]) => {
                let displayValue = value;
                let displayKey = key;
                
                // Преобразуване на ключовете за по-добро разбиране
                const keyLabels = {
                  chitalishteId: "ID на читалище",
                  feedbackId: "ID на обратна връзка",
                  entityType: "Тип обект",
                  action: "Действие",
                  performedBy: "Извършител",
                  startDate: "От дата",
                  endDate: "До дата",
                };
                
                // Форматиране на датите
                if (key === 'startDate' || key === 'endDate') {
                  try {
                    displayValue = new Date(value).toLocaleDateString('bg-BG');
                  } catch (e) {
                    // Ако не може да се форматира, остава оригиналната стойност
                  }
                }
                
                displayKey = keyLabels[key] || key;
                
                return (
                  <Chip
                    key={key}
                    label={`${displayKey}: ${displayValue}`}
                    size="small"
                    sx={{ mr: 0.5, mb: 0.5 }}
                    onDelete={() => handleFilterChange(key, "")}
                  />
                );
              })}
          </Box>
        )}
      </Paper>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }} 
          onClose={() => dispatch(clearError())}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => dispatch(clearError())}
            >
              Затвори
            </Button>
          }
        >
          {typeof error === 'string' ? error : 'Възникна грешка при зареждане на логовете'}
        </Alert>
      )}

      {/* Table */}
      {loading && logs.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Зареждане на логове...</Typography>
        </Box>
      ) : (
        <Paper sx={{ overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: "primary.main" }}>
                <TableRow>
                  <TableCell sx={{ color: "white", width: 50 }}></TableCell>
                  <TableCell sx={{ color: "white" }}>Действие</TableCell>
                  <TableCell sx={{ color: "white" }}>Тип обект</TableCell>
                  <TableCell sx={{ color: "white" }}>Читалище</TableCell>
                  <TableCell sx={{ color: "white" }}>Извършител</TableCell>
                  <TableCell sx={{ color: "white" }}>Дата и час</TableCell>
                  <TableCell sx={{ color: "white" }}>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length > 0 ? (
                  logs.map(renderTableRow)
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <HistoryToggleOff sx={{ fontSize: 48, color: "text.secondary", opacity: 0.5, mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Няма намерени логове
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {activeFiltersCount > 0 
                          ? "Няма резултати за текущите филтри" 
                          : "Все още няма записани логове в системата"}
                      </Typography>
                      {activeFiltersCount > 0 && (
                        <Button
                          variant="outlined"
                          onClick={handleClearAllFilters}
                          sx={{ mt: 2 }}
                          startIcon={<Clear />}
                        >
                          Изчисти всички филтри
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 20, 50]}
            component="div"
            count={pagination.totalElements}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Редове:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} от ${count}`
            }
            disabled={loading}
          />
        </Paper>
      )}

      {/* Details Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedLog && (
          <>
            <DialogTitle>
              Детайли на запис в лога
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Действие:</Typography>
                  <Typography variant="body1" gutterBottom>
                    <Chip 
                      label={selectedLog.action} 
                      color={getActionColor(selectedLog.action)} 
                      size="small" 
                    />
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Тип обект:</Typography>
                  <Typography variant="body1" gutterBottom>
                    {getEntityTypeLabel(selectedLog.entityType)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Извършител:</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedLog.performedBy}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Дата и час:</Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatDateTime(selectedLog.performedAt)}
                  </Typography>
                </Grid>
                {/* <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Описание:</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedLog.description || "Няма описание"}
                  </Typography>
                </Grid> */}
                {selectedLog.chitalishteName && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Читалище:</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedLog.chitalishteName}
                      {selectedLog.chitalishteRegistrationNumber && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Рег. №: {selectedLog.chitalishteRegistrationNumber}
                        </Typography>
                      )}
                    </Typography>
                  </Grid>
                )}
                {selectedLog.entityId && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">ID на обект:</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedLog.entityId}
                    </Typography>
                  </Grid>
                )}
                {selectedLog.ipAddress && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">IP адрес:</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedLog.ipAddress}
                    </Typography>
                  </Grid>
                )}
                {selectedLog.userAgent && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">Браузър:</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedLog.userAgent}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  {renderChangedFields(selectedLog)}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailDialogOpen(false)}>
                Затвори
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default AuditLogDashboard;